/**
 * Client-side trigger for Pro's monthly AI-credit allowance.
 *
 * The server decides everything (live non-pass subscription, which 30-day
 * window, once-ever per window via a unique index) — this just gives it a
 * chance to run on app load, so a member sees their credits without having to
 * start a generation first. Safe to call on every SIGNED_IN; the
 * sessionStorage guard below only avoids a redundant request per tab.
 *
 * Mirrors src/lib/signupBonus.ts, deliberately: same shape, same failure
 * behaviour (never throws, never blocks sign-in).
 */
import type { User } from '@supabase/supabase-js';
import { useCreditStore } from '../stores/creditStore';

const ATTEMPT_KEY_PREFIX = 'la_pro_credits_checked_';

/**
 * How long a "nothing owed" answer is cached per tab. A once-per-session latch
 * would be wrong: Supabase emits SIGNED_IN on the return trip from Stripe
 * Checkout, often BEFORE the webhook has written the subscription row, and
 * that first answer is a legitimate "not a member yet". Re-asking a few
 * minutes later costs one request and gets the member their credits without
 * waiting for them to start a generation.
 */
const RECHECK_AFTER_MS = 5 * 60 * 1000;

interface MonthlyGrantResponse {
  granted?: boolean;
  balance?: number;
}

export async function maybeGrantProMonthlyCredits(user: User, accessToken: string): Promise<void> {
  const attemptKey = `${ATTEMPT_KEY_PREFIX}${user.id}`;
  try {
    const last = Number(sessionStorage.getItem(attemptKey) || 0);
    if (last && Date.now() - last < RECHECK_AFTER_MS) return;
  } catch {
    // Private mode: fall through and just make the (idempotent) call.
  }

  try {
    const res = await fetch('/api/credits/monthly-grant', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    // Only a real answer starts the cooldown; a 5xx should be retried by the
    // next SIGNED_IN rather than suppressed.
    if (!res.ok) return;
    try {
      sessionStorage.setItem(attemptKey, String(Date.now()));
    } catch {
      // Private mode — the endpoint is idempotent, so repeating is harmless.
    }
    const data = (await res.json()) as MonthlyGrantResponse;
    if (data.granted && typeof data.balance === 'number') {
      // Account-scoped: a grant for a user who has since signed out must not
      // land in the next user's store.
      useCreditStore.getState().applyGrantedBalance(user.id, data.balance);
    }
  } catch {
    // Offline or transient — the server also tops up lazily on the next
    // credit-consuming request, so there is nothing to retry here.
  }
}
